/**
 * Auth gate — one question: your email.
 *
 *   - 'email' : "What's your email?" → Continue looks the email up
 *   - 'code'  : the 6-digit code we emailed (known emails only)
 *
 * A known email (an account, or a guest who saved a team before) gets a
 * code and lands on their teams. A new email goes straight on as a guest:
 * the quick team form (team name + their name), with this email as their
 * receipt email and no code. The last step offers "Save my team for next time".
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { EmbedClient } from '@/api/embed';
import { useBackOverride } from '@/state/backOverride';
import { useFlowBase } from '@/state/useFlowBase';
import '@/pages/AuthGatePage.css';

interface Props {
  client: EmbedClient;
}

const CODE_LENGTH = 6;

export function GfgAuthGatePage({ client }: Props) {
  const { base } = useFlowBase();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On the code step, the header back returns to the email step (like
  // "Use a different email"); on the email step it leaves the page.
  const backToEmail = useCallback(() => {
    setError(null);
    setStep('email');
    setCode('');
  }, []);
  useBackOverride(step === 'code' && !busy ? backToEmail : null);

  const onRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (await client.requestCode(email.trim())) {
        setStep('code');
      } else {
        // A guest starts clean: a stale token would make the team form
        // think they're signed in.
        client.signOut();
        navigate(`${base}/team-create`, { state: { email: email.trim() } });
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await client.verifyCode(email, code);
      navigate(`${base}/teams`);
    } catch (err) {
      setError('Code was invalid or has expired. Try requesting a new one.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`tn-auth tn-card${step === 'code' ? ' tn-auth--code' : ''}`}>
      <h1 className="tn-auth__title">
        {step === 'email' ? 'What\u2019s your email?' : 'Enter the code'}
      </h1>
      {step === 'code' ? <p className="tn-auth__subtitle">Sent to {email}</p> : null}

      {step === 'email' ? (
        <form onSubmit={onRequest} className="tn-auth__form">
          <label className="tn-auth__label" htmlFor="auth-email">Email</label>
          <input
            id="auth-email"
            className="tn-auth__input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            autoFocus
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
          />
          <button className="tn-auth__submit" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
        </form>
      ) : (
        <form onSubmit={onVerify} className="tn-auth__form">
          <label className="tn-auth__label tn-auth__label--sr" htmlFor="auth-code">6-digit code</label>
          {/* One real input (paste + one-time-code autofill keep working),
              stretched transparently over six boxes that mirror its value. */}
          <div className={`tn-auth__code${busy ? ' tn-auth__code--disabled' : ''}`}>
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={
                  'tn-auth__code-cell' +
                  (i < code.length ? ' tn-auth__code-cell--filled' : '') +
                  (i === Math.min(code.length, CODE_LENGTH - 1) ? ' tn-auth__code-cell--active' : '')
                }
              >
                {code[i] ?? ''}
              </span>
            ))}
            <input
              id="auth-code"
              className="tn-auth__code-input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
              disabled={busy}
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
            />
          </div>
          <button className="tn-auth__submit" type="submit" disabled={busy}>
            {busy ? 'Checking…' : 'Continue'}
          </button>
          <button
            className="tn-auth__back"
            type="button"
            onClick={backToEmail}
            disabled={busy}
          >
            Use a different email
          </button>
        </form>
      )}

      {error ? <div className="tn-auth__error" role="alert">{error}</div> : null}
    </div>
  );
}
